import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import type { GetToken } from '~/config'
import { createClient } from '~/data/graphql'
import {
  fetchAgentRequests,
  reviewNotebook,
  reviewSkillUpdate,
  setAgentSkill,
  setSkillStatus,
} from '~/data/interact'
import { theme } from '~/theme'

// The "needs you" queue, in-game: every lesson, skill draft and skill update
// the org's agents are waiting on a human for — reviewable right here with the
// same mutations the dashboard's Workforce section uses. Owner/admin only
// (the query 403s for everyone else and the HUD chip never shows).

export function RequestsPanel({
  organizationId,
  getToken,
  onClose,
  compact,
}: {
  organizationId: string
  getToken: GetToken
  onClose: () => void
  compact?: boolean
}) {
  const queryClient = useQueryClient()
  const [openId, setOpenId] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)

  const client = async () => createClient(await getToken())

  const requests = useQuery({
    queryKey: ['agentRequests', organizationId],
    queryFn: async () => fetchAgentRequests(await client(), organizationId),
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const done = (message: string) => {
    setNote(message)
    setTimeout(() => setNote(null), 2500)
    void queryClient.invalidateQueries({ queryKey: ['agentRequests', organizationId] })
  }
  const fail = (e: unknown) =>
    setNote(e instanceof Error ? e.message.slice(0, 140) : 'Review failed')

  const notebook = useMutation({
    mutationFn: async ({ versionId, approve }: { versionId: string; approve: boolean }) =>
      reviewNotebook(await client(), {
        versionId,
        approve,
        note: 'Reviewed in Glass Office',
      }),
    onSuccess: (_d, v) => done(v.approve ? 'Lesson approved ✓' : 'Lesson rejected'),
    onError: fail,
  })

  const draft = useMutation({
    mutationFn: async ({
      skillId,
      agentAccountId,
      approve,
    }: {
      skillId: string
      agentAccountId: string | null
      approve: boolean
    }) => {
      const c = await client()
      // Workforce's exact flow: activate + attach to the proposer, or archive.
      await setSkillStatus(c, { skillId, status: approve ? 'ACTIVE' : 'ARCHIVED' })
      if (approve && agentAccountId) {
        await setAgentSkill(c, { agentAccountId, skillId, attached: true })
      }
    },
    onSuccess: (_d, v) => done(v.approve ? 'Skill activated ✓' : 'Draft archived'),
    onError: fail,
  })

  const update = useMutation({
    mutationFn: async ({ proposalId, approve }: { proposalId: string; approve: boolean }) =>
      reviewSkillUpdate(await client(), {
        proposalId,
        approve,
        note: 'Reviewed in Glass Office',
      }),
    onSuccess: (_d, v) => done(v.approve ? 'Update applied ✓' : 'Update rejected'),
    onError: fail,
  })

  const data = requests.data
  const busy = notebook.isPending || draft.isPending || update.isPending

  const Row = ({
    id,
    who,
    what,
    body,
    current,
    onApprove,
    onReject,
  }: {
    id: string
    who: string
    what: string
    body: string
    current?: string | null
    onApprove: () => void
    onReject: () => void
  }) => {
    const open = openId === id
    return (
      <div style={rowBox}>
        <button onClick={() => setOpenId(open ? null : id)} style={rowHead}>
          <span style={{ color: '#ffb020', fontWeight: 700 }}>✦ {who}</span>
          <span style={{ color: theme.textDim, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
            {what}
          </span>
          <span style={{ color: theme.textDim }}>{open ? '▾' : '▸'}</span>
        </button>
        {open && (
          <div style={{ marginTop: 6 }}>
            <div style={mono}>{body || '(empty)'}</div>
            {current != null && (
              <details style={{ marginTop: 4 }}>
                <summary style={{ fontSize: 10.5, color: theme.textDim, cursor: 'pointer' }}>
                  Current version (for comparison)
                </summary>
                <div style={{ ...mono, opacity: 0.7 }}>{current || '(empty)'}</div>
              </details>
            )}
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <button onClick={onApprove} disabled={busy} style={approveBtn}>
                ✓ Approve
              </button>
              <button onClick={onReject} disabled={busy} style={rejectBtn}>
                ✗ Reject
              </button>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <aside style={compact ? sheetCompact : sheet}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.cream }}>
            ✋ Agents need you
          </div>
          <div style={{ fontSize: 11, color: theme.textDim }}>
            Lessons and skills your AI staff are waiting on
          </div>
        </div>
        <button onClick={onClose} style={closeBtn} aria-label="Close">×</button>
      </div>

      {requests.isLoading && <div style={dim}>Checking the queue…</div>}
      {requests.isError && (
        <div style={dim}>Requests aren't visible — org owners only.</div>
      )}
      {data && data.total === 0 && (
        <div style={dim}>All clear — nothing is waiting on you. 🎉</div>
      )}

      {data && data.notebooks.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={section}>Notebook lessons ({data.notebooks.length})</div>
          {data.notebooks.map((n) => (
            <Row
              key={n.versionId}
              id={`nb-${n.versionId}`}
              who={n.agentName ?? 'Agent'}
              what="wants to remember a lesson"
              body={n.content}
              onApprove={() => notebook.mutate({ versionId: n.versionId, approve: true })}
              onReject={() => notebook.mutate({ versionId: n.versionId, approve: false })}
            />
          ))}
        </div>
      )}

      {data && data.skillDrafts.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={section}>New skill drafts ({data.skillDrafts.length})</div>
          {data.skillDrafts.map((d) => (
            <Row
              key={d.skillId}
              id={`sd-${d.skillId}`}
              who={d.agentName ?? 'Agent'}
              what={`drafted “${d.skillName}”`}
              body={d.content}
              onApprove={() =>
                draft.mutate({ skillId: d.skillId, agentAccountId: d.agentAccountId, approve: true })
              }
              onReject={() =>
                draft.mutate({ skillId: d.skillId, agentAccountId: d.agentAccountId, approve: false })
              }
            />
          ))}
        </div>
      )}

      {data && data.skillUpdates.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={section}>Skill updates ({data.skillUpdates.length})</div>
          {data.skillUpdates.map((u) => (
            <Row
              key={u.proposalId}
              id={`su-${u.proposalId}`}
              who={u.agentName ?? 'Agent'}
              what={`wants to improve “${u.skillName ?? 'a skill'}”`}
              body={u.content}
              current={u.currentContent}
              onApprove={() => update.mutate({ proposalId: u.proposalId, approve: true })}
              onReject={() => update.mutate({ proposalId: u.proposalId, approve: false })}
            />
          ))}
        </div>
      )}

      {note && (
        <div style={{ fontSize: 11, marginTop: 8, color: note.includes('✓') ? theme.online : '#ffb86b' }}>
          {note}
        </div>
      )}

    </aside>
  )
}

const sheet: React.CSSProperties = {
  position: 'absolute',
  top: 70,
  right: 18,
  width: 340,
  maxHeight: 'calc(100% - 130px)',
  overflowY: 'auto',
  padding: 14,
  background: theme.panel,
  border: '1px solid #4a3d1e',
  borderRadius: 12,
  color: theme.text,
}
const sheetCompact: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  maxHeight: '72vh',
  overflowY: 'auto',
  padding: 14,
  paddingBottom: 'calc(14px + env(safe-area-inset-bottom))',
  background: theme.panel,
  borderTop: '1px solid #4a3d1e',
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  color: theme.text,
}
const closeBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: theme.textDim,
  cursor: 'pointer',
  fontSize: 20,
  lineHeight: 1,
  padding: '2px 6px',
}
const dim: React.CSSProperties = { fontSize: 11.5, color: theme.textDim, padding: '10px 0' }
const section: React.CSSProperties = {
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: theme.textDim,
  margin: '0 0 6px',
}
const rowBox: React.CSSProperties = {
  border: `1px solid ${theme.border}`,
  borderRadius: 9,
  padding: 8,
  marginBottom: 6,
  background: '#101218',
}
const rowHead: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  width: '100%',
  background: 'none',
  border: 'none',
  color: theme.text,
  fontSize: 12,
  cursor: 'pointer',
  padding: 0,
}
const mono: React.CSSProperties = {
  fontFamily: 'ui-monospace, monospace',
  fontSize: 11,
  lineHeight: 1.5,
  color: '#d5dae4',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  maxHeight: 180,
  overflowY: 'auto',
  background: '#0b0d12',
  border: `1px solid ${theme.border}`,
  borderRadius: 7,
  padding: '6px 8px',
}
const approveBtn: React.CSSProperties = {
  flex: 1,
  background: '#1d3a2a',
  border: '1px solid #2c5a40',
  color: '#7fe0a8',
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 700,
  padding: '6px 10px',
  cursor: 'pointer',
}
const rejectBtn: React.CSSProperties = {
  flex: 1,
  background: '#3a1d1d',
  border: '1px solid #5a2c2c',
  color: '#ff9a9a',
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 700,
  padding: '6px 10px',
  cursor: 'pointer',
}
